/**
 * Inline SVG badges that mirror the Pixi role icons drawn inside the
 * pod-room scene.  Kept in one place so the DOM-side surfaces
 * (HelpLegend, EventFeed, mini character badges, ...) stay
 * pixel-consistent with the canvas rendering and have a single source
 * of truth when the icon set evolves.
 */

import type { CSSProperties } from 'react';

export type ActorIconKind =
  | 'qa'
  | 'sa'
  | 'fa'
  | 'researcher'
  | 'rm'
  | 'ea'
  | 'pm'
  | 'risk_officer'
  | 'policy_updater'
  | 'leaderboard_keeper'
  | 'center_book'
  | 'discipline_gate'
  | 'system'
  | 'trader';

interface ActorIconProps {
  kind: ActorIconKind | string;
  size?: number;
  /** Accent colour (CSS hex) -- defaults to a sensible per-role tone. */
  color?: string;
  style?: CSSProperties;
  title?: string;
}

const DEFAULT_COLOR: Record<string, string> = {
  qa: '#58a6ff',
  sa: '#db61a2',
  fa: '#d29922',
  researcher: '#58a6ff',
  rm: '#d29922',
  ea: '#2ea043',
  pm: '#ffd33d',
  risk_officer: '#f85149',
  policy_updater: '#a371f7',
  leaderboard_keeper: '#ffd33d',
  center_book: '#8b949e',
  discipline_gate: '#a371f7',
  system: '#8b949e',
  trader: '#2ea043',
};

export function ActorIcon({ kind, size = 16, color, style, title }: ActorIconProps) {
  const accent = color ?? DEFAULT_COLOR[kind] ?? '#8b949e';
  switch (kind) {
    case 'qa':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" style={style} aria-label={title}>
          <rect x="2" y="9" width="3" height="5" fill={accent} />
          <rect x="6.5" y="5" width="3" height="9" fill={accent} />
          <rect x="11" y="7" width="3" height="7" fill={accent} />
        </svg>
      );
    case 'sa':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" style={style} aria-label={title}>
          <rect x="1.5" y="2" width="13" height="9" rx="3" fill={accent} />
          <path d="M5 11 L4 14 L8 11 Z" fill={accent} />
        </svg>
      );
    case 'fa':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" style={style} aria-label={title}>
          <rect x="3" y="2" width="10" height="12" rx="1" fill={accent} />
          <rect x="5" y="0.5" width="6" height="2.5" rx="0.5" fill="#0d1117" />
          <rect x="5" y="6" width="6" height="1" fill="#0d1117" />
          <rect x="5" y="9" width="4" height="1" fill="#0d1117" />
        </svg>
      );
    case 'researcher':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" style={style} aria-label={title}>
          <circle cx="7" cy="7" r="4" stroke={accent} strokeWidth="1.6" fill="none" />
          <line x1="10" y1="10" x2="14" y2="14" stroke={accent} strokeWidth="1.6" />
        </svg>
      );
    case 'rm':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" style={style} aria-label={title}>
          <path d="M8 0 L14 2 L14 8 Q14 14 8 16 Q2 14 2 8 L2 2 Z" fill={accent} />
          <rect x="7" y="4" width="2" height="7" fill="#0d1117" />
          <rect x="5" y="6.5" width="6" height="2" fill="#0d1117" />
        </svg>
      );
    case 'ea':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" style={style} aria-label={title}>
          <path d="M8 0 L13 6 L10 6 L10 16 L6 16 L6 6 L3 6 Z" fill={accent} />
        </svg>
      );
    case 'pm':
      return (
        <svg width={size * 1.15} height={size * 0.8} viewBox="0 0 20 14" style={style} aria-label={title}>
          <path
            d="M0 13 L0 5 L4 8 L7 0 L10 8 L13 0 L16 8 L20 5 L20 13 Z"
            fill={accent}
          />
          <circle cx="7" cy="4" r="0.9" fill="#ffffff" />
          <circle cx="10" cy="3" r="1.1" fill="#ffffff" />
          <circle cx="13" cy="4" r="0.9" fill="#ffffff" />
        </svg>
      );
    case 'risk_officer':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" style={style} aria-label={title}>
          <path d="M8 1 L15 14 L1 14 Z" fill={accent} />
          <rect x="7" y="6" width="2" height="4" fill="#0d1117" />
          <circle cx="8" cy="12" r="1" fill="#0d1117" />
        </svg>
      );
    case 'policy_updater':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" style={style} aria-label={title}>
          <circle cx="8" cy="8" r="5" fill={accent} />
          <circle cx="8" cy="8" r="2.2" fill="#0d1117" />
        </svg>
      );
    case 'leaderboard_keeper':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" style={style} aria-label={title}>
          <path d="M3 1 L13 1 L11 8 L5 8 Z" fill={accent} />
          <rect x="7" y="8" width="2" height="3" fill={accent} />
          <rect x="4" y="11" width="8" height="2" fill={accent} />
        </svg>
      );
    case 'center_book':
    case 'discipline_gate':
    case 'system':
    case 'trader':
    default:
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" style={style} aria-label={title}>
          <circle cx="8" cy="8" r="6" fill={accent} />
        </svg>
      );
  }
}
